import { HttpUploadProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Content, FileContent } from '@repositories/models';
import { RepositoryService } from '@repositories/services';

import {
    BehaviorSubject,
    firstValueFrom,
    lastValueFrom,
    Subject
} from 'rxjs';

import { tap } from 'rxjs/operators';


export type ManagedUploadState =
    | 'checking'
    | 'options'
    | 'queued'
    | 'duplicate'
    | 'uploading'
    | 'completed'
    | 'skipped'
    | 'error';


export interface ManagedUploadItem {
    id: string;
    file: File;
    fileInfo: FileContent;
    content: Content;
    relativePath: string;
    state: ManagedUploadState;
    progress: number;
    bookView: boolean;
}


export interface UploadStatus {
    active: boolean;
    checking: boolean;
    progress: number;
    currentFile: string;
    current: number;
    total: number;
    items: ManagedUploadItem[];
    duplicates: ManagedUploadItem[];
    processing: boolean;
}


/*
 * Se mantienen estas interfaces porque el modal antiguo
 * todavía utiliza prepareFiles() y upload().
 */
export interface UploadItem {
    file: File;
    fileInfo: FileContent;
}


export interface PrepareUploadResult {
    uploadItems: UploadItem[];
    duplicateFiles: string[];
}


@Injectable({
    providedIn: 'root'
})
export class UploadManagerService {

    private _status = new BehaviorSubject<UploadStatus>({
        active: false,
        checking: false,
        progress: 0,
        currentFile: '',
        current: 0,
        total: 0,
        items: [],
        duplicates: [],
        processing: false
    });


    public status$ = this._status.asObservable();
    private _completed = new Subject<void>();
    public completed$ = this._completed.asObservable();
    private _items: ManagedUploadItem[] = [];
    private _repositoryService: RepositoryService = null;
    private _content: Content = null;
    private _checking: boolean = false;
    private _processing: boolean = false;
    private _completionEmitted: boolean = false;
    private _idCounter: number = 0;
    private _awaitingResponse: boolean = false;
    private _panelOpen = new BehaviorSubject<boolean>(false);
    public panelOpen$ = this._panelOpen.asObservable();

    openPanel(): void { this._panelOpen.next(true); }
    closePanel(): void { this._panelOpen.next(false); }
    togglePanel(): void { this._panelOpen.next(!this._panelOpen.value); }

    /* =====================================================
       NUEVO FLUJO
       ===================================================== */

    async addFiles(files: File[], content: Content, repositoryService: RepositoryService): Promise<void> {

        if (!files || files.length === 0) {
            return;
        }

        if (!this.hasPendingWork()) {
            this._items = [];
            this._completionEmitted = false;
            this.closePanel();
        }


        this._content = content;
        this._repositoryService = repositoryService;


        const validFiles = files.filter(file => {

            const unsupported =
                file.type === '' &&
                (
                    file.size === 0 ||
                    file.name.indexOf('.') === -1 ||
                    file.name.split('.')[0] === ''
                );

            return !unsupported;
        });


        if (validFiles.length === 0) {
            return;
        }


        const newItems: ManagedUploadItem[] = validFiles.map(file => ({
            id: `upload-${++this._idCounter}`,
            file,
            fileInfo: null,
            content: {
                repository: content.repository,
                ancestors: [...content.ancestors]
            } as Content,
            relativePath: file.name,
            state: 'checking',
            progress: 0,
            bookView: false
        }));

        this._items.push(...newItems);

        this._checking = true;
        this.publishStatus();

        for (const item of newItems) {

            try {

                const existingFile = await firstValueFrom(
                    repositoryService.checkExistingFile(
                        item.file,
                        item.content
                    )
                );


                const newFileInfo = repositoryService.newFileInfo(item.file);
                const isPdf = newFileInfo.contentType === 'PDF';

                if (existingFile) {
                    existingFile.title = newFileInfo.title;
                    existingFile.contentType = newFileInfo.contentType;
                    if (isPdf) existingFile.bookView = false;

                    item.fileInfo = existingFile;
                    item.state = 'duplicate';
                    this.openPanel();
                } else if (isPdf) {
                    newFileInfo.bookView = false;
                    item.fileInfo = newFileInfo;
                    item.state = 'options';
                    this.openPanel();
                } else {
                    item.fileInfo = null;
                    item.state = 'queued';
                }

            } catch (error) {

                console.error('Error comprobando archivo:', item.file.name, error);

                item.state = 'error';
                item.progress = 100;
            }

            this.publishStatus();
        }

        this._checking = false;
        this.publishStatus();


        /*
         * Los que NO son duplicados empiezan
         * a subir automáticamente.
         */
        await this.processQueue();
    }

    async addFolderFiles(files: File[], content: Content, repositoryService: RepositoryService): Promise<void> {

        if (!files || files.length === 0) {
            return;
        }
        if (!this.hasPendingWork()) {
            this._items = [];
            this._completionEmitted = false;
            this.closePanel();
        }

        this._repositoryService = repositoryService;
        const validFiles = files.filter(file => {
            const unsupported =
                file.type === '' &&
                (
                    file.size === 0 ||
                    file.name.indexOf('.') === -1 ||
                    file.name.split('.')[0] === ''
                );
            return !unsupported;
        });

        if (validFiles.length === 0) {
            return;
        }

        this._checking = true;
        this.publishStatus();

        try {

            /*
             * ==================================================
             * 1. EXTRAER TODAS LAS CARPETAS
             * ==================================================
             */

            const folderPaths = this.extractFolderPaths(validFiles);
            const folderAncestorsMap = new Map<string, string[]>();

            folderAncestorsMap.set('', [...content.ancestors]);

            /*
             * ==================================================
             * 2. CREAR O REUTILIZAR CARPETAS
             * ==================================================
             */

            for (const folderPath of folderPaths) {
                const separatorIndex = folderPath.lastIndexOf('/');
                const parentPath = separatorIndex >= 0 ? folderPath.substring(0, separatorIndex) : '';
                const folderName = separatorIndex >= 0 ? folderPath.substring(separatorIndex + 1) : folderPath;
                const parentAncestors = folderAncestorsMap.get(parentPath) ?? [...content.ancestors];

                let folder = await firstValueFrom(repositoryService.checkExistingFolder(folderName, content.repository, parentAncestors));
                if (!folder) {
                    folder = await repositoryService.createFolder(folderName, content.repository, parentAncestors);
                }

                folderAncestorsMap.set(folderPath, [...parentAncestors, folder.id]);
            }


            /*
             * ==================================================
             * 3. CREAR ITEMS PARA LA COLA
             * ==================================================
             */

            const newItems: ManagedUploadItem[] = validFiles.map(file => {
                const relativePath = (file as any).webkitRelativePath || file.name;
                const slash = relativePath.lastIndexOf('/');
                const folderPath = slash >= 0 ? relativePath.substring(0, slash) : '';
                const ancestors = folderAncestorsMap.get(folderPath) ?? [...content.ancestors];

                return {
                    id: `upload-${++this._idCounter}`,
                    file,
                    fileInfo: null,
                    content: {
                        repository: content.repository,
                        ancestors: [...ancestors]
                    } as Content,
                    relativePath,
                    state: 'checking' as ManagedUploadState,
                    progress: 0,
                    bookView: false
                };
            });

            this._items.push(...newItems);
            this.publishStatus();


            /*
             * ==================================================
             * 4. COMPROBAR ARCHIVOS DUPLICADOS
             * ==================================================
             */

            for (const item of newItems) {
                try {
                    const existingFile = await firstValueFrom(repositoryService.checkExistingFile(item.file, item.content));

                    const newFileInfo = repositoryService.newFileInfo(item.file);
                    const isPdf = newFileInfo.contentType === 'PDF';

                    if (existingFile) {
                        existingFile.title = newFileInfo.title;
                        existingFile.contentType = newFileInfo.contentType;
                        if (isPdf) existingFile.bookView = false;

                        item.fileInfo = existingFile;
                        item.state = 'duplicate';
                        this.openPanel();
                    } else if (isPdf) {
                        newFileInfo.bookView = false;
                        item.fileInfo = newFileInfo;
                        item.state = 'options';
                        this.openPanel();
                    } else {
                        item.fileInfo = null;
                        item.state = 'queued';
                    }

                } catch (error) {
                    console.error('Error comprobando archivo:', item.relativePath, error);
                    item.state = 'error';
                    item.progress = 100;
                }

                this.publishStatus();
            }

        } catch (error) {
            console.error('Error preparando carpeta:', error);
        } finally {
            this._checking = false;
            this.publishStatus();
        }

        await this.processQueue();
    }

    setBookView(id: string, bookView: boolean): void {
        const item = this._items.find(value => value.id === id);
        if (!item) return;

        item.bookView = bookView;
        if (item.fileInfo) item.fileInfo.bookView = bookView;

        this.publishStatus();
    }

    confirmPdfOptions(id: string): void {
        const item = this._items.find(value => value.id === id);
        if (!item || item.state !== 'options') return;

        if (item.fileInfo) item.fileInfo.bookView = item.bookView;

        item.state = 'queued';
        this.publishStatus();

        void this.processQueue();
    }

    replaceDuplicate(id: string): void {
        const item = this._items.find(value => value.id === id);
        if (!item || item.state !== 'duplicate') return;

        if (item.fileInfo?.contentType === 'PDF') item.fileInfo.bookView = item.bookView;

        item.state = 'queued';
        item.progress = 0;

        this.publishStatus();
        void this.processQueue();
    }


    skipDuplicate(id: string): void {

        const item = this._items.find(
            value => value.id === id
        );


        if (!item || item.state !== 'duplicate') {
            return;
        }

        item.state = 'skipped';
        item.progress = 100;

        this.publishStatus();
        this.finishIfDone();
    }


    private async processQueue(): Promise<void> {

        /*
         * Evita iniciar dos procesadores al mismo tiempo.
         */
        if (this._processing) {
            return;
        }


        this._processing = true;

        this.publishStatus();


        try {
            while (true) {
                const item = this._items.find(value => value.state === 'queued');
                if (!item) {
                    break;
                }
                item.state = 'uploading';
                item.progress = 0;
                this._awaitingResponse = false;

                this.publishStatus();

                try {

                    await lastValueFrom(
                        this._repositoryService.uploadFile(
                            item.fileInfo,
                            item.file,
                            item.content
                        )
                            .pipe(
                                tap(response => {
                                    const event = response as HttpUploadProgressEvent;if (event && typeof event.loaded === 'number') {

                                        const rawProgress = event.total
                                            ? Math.round(
                                                100 *
                                                event.loaded /
                                                event.total
                                            )
                                            : 0;

                                        if (rawProgress >= 100) {

                                            this._awaitingResponse = true;

                                            item.progress = 99;

                                        } else {

                                            this._awaitingResponse = false;

                                            item.progress = rawProgress;
                                        }


                                        this.publishStatus();
                                    }

                                })
                            )
                    );
                    this._awaitingResponse = false;

                    item.progress = 100;
                    item.state = 'completed';

                } catch (error) {
                    this._awaitingResponse = false;
                    console.error('Error subiendo archivo:', item.file.name, error);
                    item.progress = 100;
                    item.state = 'error';
                }
                this.publishStatus();
            }

        } finally {
            this._processing = false;
            this.publishStatus();
            this.finishIfDone();
        }
    }


    private finishIfDone(): void {

        if (this.hasPendingWork()) {
            return;
        }


        if (this._items.length > 0 && !this._completionEmitted) {
            this._completionEmitted = true;
            this.closePanel();
            this.publishStatus(false);

            /*
             * RepositoryFolderComponent escucha este evento
             * y refresca los archivos.
             */
            this._completed.next();
        }
    }


    private hasPendingWork(): boolean {

        return (
            this._checking ||
            this._processing ||
            this._items.some(item =>
                item.state === 'checking' ||
                item.state === 'options' ||
                item.state === 'queued' ||
                item.state === 'uploading' ||
                item.state === 'duplicate'
            )
        );
    }


    private publishStatus(forceActive?: boolean): void {

        const total = this._items.length;


        const duplicates = this._items.filter(item => item.state === 'duplicate');

        const uploading = this._items.find(item => item.state === 'uploading');


        const finished =
            this._items.filter(item =>
                item.state === 'completed' ||
                item.state === 'skipped' ||
                item.state === 'error'
            ).length;


        const calculatedProgress =
            total > 0
                ? Math.floor(
                    this._items.reduce(
                        (sum, item) => sum + item.progress,
                        0
                    ) / total
                )
                : 0;


        const totalProgress =
            this.hasPendingWork()
                ? Math.min(calculatedProgress, 99)
                : 100;

        let current = finished;

        if (uploading) {
            current++;
        }

        current = Math.min(current, total);

        this._status.next({

            active: forceActive !== undefined ? forceActive : this.hasPendingWork(),
            checking: this._checking,
            progress: totalProgress,
            currentFile: uploading?.file?.name || '',
            current: current,
            total: total,
            items: [...this._items],
            duplicates: [...duplicates],
            processing: this._awaitingResponse
        });
    }


    /* =====================================================
       COMPATIBILIDAD CON EL MODAL ANTIGUO
       ===================================================== */

    async prepareFiles(files: File[], content: Content, repositoryService: RepositoryService): Promise<PrepareUploadResult> {

        const uploadItems: UploadItem[] = [];

        const duplicateFiles: string[] = [];

        for (const file of files) {
            let fileInfo: FileContent = null;
            const existingFile = await firstValueFrom(
                repositoryService.checkExistingFile(
                    file,
                    content
                )
            );


            if (existingFile) {
                duplicateFiles.push(file.name);
                fileInfo = existingFile;
                const newFileInfo = repositoryService.newFileInfo(file);
                fileInfo.title = newFileInfo.title;
                fileInfo.contentType = newFileInfo.contentType;
            }

            uploadItems.push({file: file, fileInfo: fileInfo});
        }

        return {uploadItems: uploadItems, duplicateFiles: duplicateFiles
        };
    }


    async upload(uploadItems: UploadItem[], content: Content, repositoryService: RepositoryService): Promise<void> {

        if (!uploadItems.length) {return;}
        const total = uploadItems.length;

        this._status.next({
            active: true,
            checking: false,
            progress: 0,
            currentFile: uploadItems[0]?.file?.name || '',
            current: 1,
            total: total,
            items: [],
            duplicates: [],
            processing: false,
        });


        try {

            for (let i = 0; i < uploadItems.length; i++) {
                const file = uploadItems[i].file;
                const fileInfo = uploadItems[i].fileInfo;
                await lastValueFrom(

                    repositoryService
                        .uploadFile(fileInfo, file, content)
                        .pipe(
                            tap(response => {
                                const event = response as HttpUploadProgressEvent;

                                if (event && typeof event.loaded === 'number') {
                                    const fileProgress =
                                        event.total
                                            ? Math.round(
                                                100 *
                                                event.loaded /
                                                event.total
                                            )
                                            : 0;

                                    const globalProgress =
                                        Math.round(
                                            ((i * 100) + fileProgress) / total
                                        );

                                    this._status.next({
                                        active: true,
                                        checking: false,
                                        progress: globalProgress,
                                        currentFile: file.name,
                                        current: i + 1,
                                        total: total,
                                        items: [],
                                        duplicates: [],
                                        processing: false,
                                    });
                                }
                            })
                        )
                );
            }


            this._status.next({
                active: false,
                checking: false,
                progress: 100,
                currentFile: '',
                current: total,
                total: total,
                items: [],
                duplicates: [],
                processing: false,
            });
            this._completed.next();

        } catch (error) {
            this._status.next({
                active: false,
                checking: false,
                progress: 0,
                currentFile: '',
                current: 0,
                total: total,
                items: [],
                duplicates: [],
                processing: false,
            });

            throw error;
        }
    }

    private extractFolderPaths(files: File[]): string[] {
        const folders = new Set<string>();
        for (const file of files) {
            const relativePath = (file as any).webkitRelativePath || file.name;
            const parts = relativePath.split('/').filter(value => !!value);

            for (let i = 1; i < parts.length; i++) {
                folders.add(parts.slice(0, i).join('/'));
            }
        }

        return Array.from(folders).sort((a, b) => {
                const depthA = a.split('/').length;
                const depthB = b.split('/').length;

                if (depthA !== depthB) {
                    return depthA - depthB;
                }
                return a.localeCompare(b, 'es', {sensitivity: 'base'});
            });
    }
}